'use client'

/**
 * The scoping question on a Home turn (#16823, part of #16812).
 *
 * When a question could mean two answerable things, the turn asks ONE
 * multi-choice question instead of answering (`resolveScope`,
 * `@/lib/ai-chat/scope-question`). This renders it:
 *
 *  - one button per option — pressing it asks that option's question as the
 *    reader's next turn, carrying the pick so the server can hold the reading
 *    for the rest of the conversation;
 *  - "Something else", which opens a free-text field. What the reader types
 *    is the next question, flagged as free text — never sent as the pick.
 *
 * Every control is a native button or input, so Tab reaches each and Enter
 * operates it. Opening "Something else" moves focus into the field.
 */

import { type FormEvent, useEffect, useId, useRef, useState } from 'react'

import { HelpCircle } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MAX_MESSAGE_LENGTH } from '@/lib/ai-chat/client-text-budget'
import type { ScopeStep } from '@/lib/ai-chat/next-steps'
import {
  parseScopeAnswer,
  type ScopeAnswer,
  SOMETHING_ELSE_LABEL,
} from '@/lib/ai-chat/scope-question'

export function ScopeQuestion({
  step,
  onAnswer,
  disabled = false,
}: {
  step: ScopeStep
  /** Ask `message` as the reader's next turn, carrying the pick. */
  onAnswer: (message: string, answer: ScopeAnswer | null) => void
  /** True while a turn streams, or once the reader has replied. */
  disabled?: boolean
}) {
  const headingId = useId()
  const formId = useId()
  const fieldId = useId()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const field = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) field.current?.focus()
  }, [open])

  function submit(event: FormEvent) {
    event.preventDefault()
    const message = text.trim()
    if (!message || disabled) return
    onAnswer(message, { freeText: true })
  }

  return (
    <section
      data-testid="ai-chat-scope-question"
      aria-labelledby={headingId}
      className="mt-1"
    >
      <h4
        id={headingId}
        className="flex items-center gap-1.5 text-base font-medium text-text-primary"
      >
        <HelpCircle
          className="h-4 w-4 shrink-0 text-text-secondary"
          aria-hidden
        />
        {step.question}
      </h4>
      <ul className="mt-2 flex flex-wrap gap-2">
        {step.options.map((option, optionIndex) => (
          <li key={option.id}>
            <Chip
              variant="outline"
              className="pointer-coarse:min-h-11"
              disabled={disabled}
              // The id is an intent from a closed list; a pick that does not
              // parse still asks the question, it just carries no pick.
              onClick={() =>
                onAnswer(
                  option.question,
                  parseScopeAnswer({ intent: option.id, optionIndex }),
                )
              }
            >
              {option.label}
            </Chip>
          </li>
        ))}
        <li>
          <Chip
            variant="outline"
            className="pointer-coarse:min-h-11"
            disabled={disabled}
            aria-expanded={open}
            aria-controls={open ? formId : undefined}
            onClick={() => setOpen(current => !current)}
          >
            {SOMETHING_ELSE_LABEL}
          </Chip>
        </li>
      </ul>
      {open && (
        <form
          id={formId}
          onSubmit={submit}
          className="mt-2 flex items-end gap-2"
        >
          <div className="min-w-0 flex-1">
            <Label htmlFor={fieldId}>What did you mean?</Label>
            <Input
              ref={field}
              id={fieldId}
              value={text}
              maxLength={MAX_MESSAGE_LENGTH}
              disabled={disabled}
              onChange={event => setText(event.target.value)}
            />
          </div>
          <Button
            type="submit"
            size="sm"
            className="pointer-coarse:min-h-11"
            disabled={disabled || text.trim().length === 0}
          >
            Ask
          </Button>
        </form>
      )}
    </section>
  )
}
